import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Loader } from '../../components/loader';
import { Tabs } from '../../components/ui/tabs';
import { CountrySelect } from '../../components/ui/country-select';
import supabase from '../../supabase';
import { ArrowLeft } from 'lucide-react';
import PhoneInput from 'react-phone-number-input';
import { getErrorMessage } from '../../helpers/get-error-message';

const RESET_TABS = [
  { id: 'email', label: 'Email' },
  { id: 'phone', label: 'Phone' },
];

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [resetMethod, setResetMethod] = useState(
    searchParams.get('method') === 'phone' ? 'phone' : 'email',
  );
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState<string | undefined>();
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [isOtpSent, setIsOtpSent] = useState(false);

  const submitEmailReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;
      setIsEmailSent(true);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to send reset email'));
    } finally {
      setIsLoading(false);
    }
  };

  const sendPhoneOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone) return;
    setIsLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.signInWithOtp({ phone });

      if (error) throw error;
      setIsOtpSent(true);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to send OTP'));
    } finally {
      setIsLoading(false);
    }
  };

  const verifyPhoneOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone) return;
    setIsLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.verifyOtp({
        phone,
        token: otp,
        type: 'sms',
      });

      if (error) throw error;
      navigate('/reset-password');
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to verify OTP'));
    } finally {
      setIsLoading(false);
    }
  };

  if (isEmailSent) {
    return (
      <div className="flex flex-col min-h-screen bg-bg-main">
        <div className="flex items-center p-4">
          <button
            onClick={() => navigate('/sign-in')}
            className="hover:text-accent-purple transition-colors absolute left-4"
          >
            <ArrowLeft className="h-5 w-5 text-title" />
          </button>
          <h1 className="text-lg font-medium text-center flex-1 text-title">
            Check Your Email
          </h1>
        </div>

        <div className="flex justify-center my-6">
          <div className="w-16 h-16">
            <img
              src="/images/llaami.svg"
              alt="Llaami Logo"
              className="w-full h-full"
            />
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-sm space-y-6">
            <div className="space-y-2 text-center">
              <p className="text-subheading">
                We've sent you a password reset link. Please check your email
                and follow the instructions to reset your password.
              </p>
            </div>
            <Button
              onClick={() => navigate('/sign-in')}
              className="w-full bg-accent-purple hover:bg-accent-purple/90 text-white rounded-full py-6 flex items-center justify-center h-12"
            >
              Back to Sign In
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-bg-main">
      <div className="flex items-center p-4">
        <button
          onClick={() => navigate('/sign-in')}
          className="hover:text-accent-purple transition-colors absolute left-4"
        >
          <ArrowLeft className="h-5 w-5 text-title" />
        </button>
        <h1 className="text-lg font-medium text-center flex-1 text-title">
          Forgot Password
        </h1>
      </div>

      <div className="flex justify-center my-6">
        <div className="w-16 h-16">
          <img
            src="/images/llaami.svg"
            alt="Llaami Logo"
            className="w-full h-full"
          />
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-sm space-y-6">
          <Tabs
            activeTab={resetMethod}
            onTabChange={setResetMethod}
            tabs={RESET_TABS}
          />

          {resetMethod === 'email' ? (
            <>
              <div className="space-y-2 text-center">
                <p className="text-subheading">
                  Enter your email address and we'll send you a link to reset
                  your password.
                </p>
              </div>

              <form onSubmit={submitEmailReset} className="space-y-4">
                <div className="space-y-2">
                  <Input
                    id="email"
                    placeholder="name@example.com"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                    disabled={isLoading}
                    className="h-12 bg-white border-outline focus:border-accent-purple focus:ring-accent-purple focus:outline-none"
                    style={{ fontSize: '16px' }}
                  />
                  {error && (
                    <p className="text-sm text-incorrect text-center">
                      {error}
                    </p>
                  )}
                </div>
                <Button
                  type="submit"
                  className="w-full bg-accent-purple hover:bg-accent-purple/90 text-white rounded-full py-6 flex items-center justify-center h-12"
                  disabled={isLoading || !email}
                >
                  {isLoading ? <Loader /> : 'Send Reset Link'}
                </Button>
              </form>
            </>
          ) : isOtpSent ? (
            <>
              <div className="space-y-2 text-center">
                <p className="text-subheading">
                  Enter the 6-digit code sent to your phone.
                </p>
              </div>

              <form onSubmit={verifyPhoneOtp} className="space-y-4">
                <div className="space-y-2">
                  <Input
                    id="otp"
                    placeholder="Enter 6-digit code"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={otp}
                    onChange={(e) =>
                      setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))
                    }
                    required
                    disabled={isLoading}
                    className="h-12 bg-white border-outline focus:border-accent-purple focus:ring-accent-purple focus:outline-none text-center tracking-widest text-lg"
                    style={{ fontSize: '16px' }}
                  />
                  {error && (
                    <p className="text-sm text-incorrect text-center">
                      {error}
                    </p>
                  )}
                </div>
                <Button
                  type="submit"
                  className="w-full bg-accent-purple hover:bg-accent-purple/90 text-white rounded-full py-6 flex items-center justify-center h-12"
                  disabled={isLoading || otp.length !== 6}
                >
                  {isLoading ? <Loader /> : 'Verify Code'}
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    setIsOtpSent(false);
                    setOtp('');
                    setError(null);
                  }}
                  className="w-full text-sm text-accent-purple hover:text-accent-purple/80"
                >
                  Resend code
                </button>
              </form>
            </>
          ) : (
            <>
              <div className="space-y-2 text-center">
                <p className="text-subheading">
                  Enter your phone number and we'll send you a verification
                  code.
                </p>
              </div>

              <form onSubmit={sendPhoneOtp} className="space-y-4">
                <div className="space-y-2">
                  <PhoneInput
                    id="phone"
                    defaultCountry="ZA"
                    international
                    countryCallingCodeEditable={false}
                    value={phone}
                    onChange={setPhone}
                    placeholder="79 123 4567"
                    inputComponent={Input}
                    countrySelectComponent={CountrySelect}
                    numberInputProps={{
                      className:
                        'h-12 bg-white border-outline focus:border-accent-purple focus:ring-accent-purple focus:outline-none',
                      style: { fontSize: '16px' },
                    }}
                    className="flex gap-2 items-center"
                  />
                  {error && (
                    <p className="text-sm text-incorrect text-center">
                      {error}
                    </p>
                  )}
                </div>
                <Button
                  type="submit"
                  className="w-full bg-accent-purple hover:bg-accent-purple/90 text-white rounded-full py-6 flex items-center justify-center h-12"
                  disabled={isLoading || !phone}
                >
                  {isLoading ? <Loader /> : 'Send Verification Code'}
                </Button>
              </form>
            </>
          )}

          <div className="text-center">
            <button
              onClick={() => navigate('/sign-in')}
              className="text-accent-purple hover:text-accent-purple/80"
            >
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
